import { api } from './api';

export type PromoCodeType = 'fixed' | 'percentage';

export interface DomainValue {
  id: string;
  name: string;
}

export interface PromoCodeDto {
  id: string;
  promoCodeId: number | null;
  code: string;
  type: PromoCodeType;
  value: number;
  oneTime: boolean;
  minOrderAmount: number | null;
  startDate: string | null;
  endDate: string | null;
  domains: DomainValue[];
  active: boolean;
  isRedeemed: boolean;
  createdAt: string;
  updatedAt: string;
  createdByAdminId: string;
  createdByAdmin: string;
  timesUsed: number;
}

export type PromoCodesListParams = {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | 'active' | 'inactive' | 'redeemed';
  type?: 'all' | PromoCodeType;
  dateFrom?: string;
  dateTo?: string;
  domains?: string[];
};

export interface PromoCodesListResponse {
  items: PromoCodeDto[];
  total: number;
  page: number;
  limit: number;
  availableDomains: DomainValue[];
}

export interface PromoCodeOrderUsageDto {
  orderId: number;
  reference: string;
  status: string;
  createdAt: string;
  clientPriceAmount: number;
  clientPriceCurrency: string;
  discountAmount: number;
}

export interface PromoCodeDetailsResponse {
  promoCode: PromoCodeDto;
  orders: PromoCodeOrderUsageDto[];
}

export interface CreatePromoCodePayload {
  code: string;
  type: PromoCodeType;
  value: number;
  oneTime: boolean;
  minOrderAmount?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  domains?: DomainValue[];
  active: boolean;
}

export interface GeneratePromoCodesPayload {
  amount: number;
  type: PromoCodeType;
  value: number;
  oneTime: boolean;
  minOrderAmount?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  domains?: DomainValue[];
  active: boolean;
}

export interface UpdatePromoCodePayload {
  value: number;
  minOrderAmount?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  domains?: DomainValue[];
  active: boolean;
}

type ApiDomainValue = {
  id: number;
  name: string;
};

type ApiPromoCodeDto = Omit<PromoCodeDto, 'domains'> & { domains: ApiDomainValue[] };

type ApiPromoCodesListResponse = Omit<PromoCodesListResponse, 'items' | 'availableDomains'> & {
  items: ApiPromoCodeDto[];
  availableDomains: ApiDomainValue[];
};

type ApiPromoCodeDetailsResponse = {
  promoCode: ApiPromoCodeDto;
  orders: PromoCodeOrderUsageDto[];
};

const toDomainValues = (values?: ApiDomainValue[] | null): DomainValue[] =>
  Array.isArray(values)
    ? values
      .filter((value) => Number.isInteger(value.id))
      .map((value) => ({
        id: String(value.id),
        name: typeof value.name === 'string' && value.name.trim() ? value.name : String(value.id),
      }))
    : [];

const toDomainIdsNumbers = (values?: string[]): number[] | undefined => {
  if (!values || values.length === 0) {
    return undefined;
  }

  const parsed = values
    .map((value) => Number.parseInt(value, 10))
    .filter((value) => Number.isInteger(value));

  return parsed.length > 0 ? parsed : undefined;
};

const toApiDomainValues = (values?: DomainValue[]): ApiDomainValue[] | undefined => {
  if (!values || values.length === 0) {
    return undefined;
  }

  const unique = new Map<number, ApiDomainValue>();

  values.forEach((value) => {
    const id = Number.parseInt(value.id, 10);
    if (!Number.isInteger(id)) {
      return;
    }

    const name = value.name?.trim() ? value.name.trim() : String(id);
    unique.set(id, { id, name });
  });

  return unique.size > 0 ? Array.from(unique.values()) : undefined;
};

const mapApiPromoCode = (item: ApiPromoCodeDto): PromoCodeDto => ({
  ...item,
  domains: toDomainValues(item.domains),
});

const createListSearchParams = (params?: PromoCodesListParams) => {
  const searchParams = new URLSearchParams();

  if (params?.page) {
    searchParams.set('page', String(params.page));
  }

  if (params?.limit) {
    searchParams.set('limit', String(params.limit));
  }

  if (params?.search?.trim()) {
    searchParams.set('search', params.search.trim());
  }

  if (params?.status && params.status !== 'all') {
    searchParams.set('status', params.status);
  }

  if (params?.type && params.type !== 'all') {
    searchParams.set('type', params.type);
  }

  if (params?.dateFrom) {
    searchParams.set('dateFrom', params.dateFrom);
  }

  if (params?.dateTo) {
    searchParams.set('dateTo', params.dateTo);
  }

  const domainIds = toDomainIdsNumbers(params?.domains);
  if (domainIds && domainIds.length > 0) {
    domainIds.forEach((domainId) => {
      searchParams.append('domains', String(domainId));
    });
  }

  return searchParams;
};

export const promoCodesService = {
  list: async (params?: PromoCodesListParams): Promise<PromoCodesListResponse> => {
    const searchParams = createListSearchParams(params);
    const suffix = searchParams.toString();
    const response = await api.get<ApiPromoCodesListResponse>(
      `/api/promocodes${suffix ? `?${suffix}` : ''}`
    );

    return {
      ...response,
      items: response.items.map(mapApiPromoCode),
      availableDomains: toDomainValues(response.availableDomains),
    };
  },

  createSingle: async (payload: CreatePromoCodePayload): Promise<PromoCodeDto> => {
    const response = await api.post<ApiPromoCodeDto>('/api/promocodes', {
      ...payload,
      domains: toApiDomainValues(payload.domains) ?? [],
    });

    return mapApiPromoCode(response);
  },

  createBulk: async (payload: GeneratePromoCodesPayload): Promise<PromoCodeDto[]> => {
    const response = await api.post<ApiPromoCodeDto[]>('/api/promocodes/bulk', {
      ...payload,
      domains: toApiDomainValues(payload.domains) ?? [],
    });

    return response.map(mapApiPromoCode);
  },

  update: async (id: string, payload: UpdatePromoCodePayload): Promise<PromoCodeDto> => {
    const response = await api.patch<ApiPromoCodeDto>(`/api/promocodes/${id}`, {
      ...payload,
      domains: toApiDomainValues(payload.domains) ?? [],
    });

    return mapApiPromoCode(response);
  },

  setActive: async (id: string, active: boolean): Promise<PromoCodeDto> => {
    const response = await api.patch<ApiPromoCodeDto>(`/api/promocodes/${id}/active`, { active });
    return mapApiPromoCode(response);
  },

  getDetails: async (id: string): Promise<PromoCodeDetailsResponse> => {
    const response = await api.get<ApiPromoCodeDetailsResponse>(`/api/promocodes/${id}/details`);

    return {
      promoCode: mapApiPromoCode(response.promoCode),
      orders: response.orders,
    };
  },
};
